<template>
    <div class="cars-list-item">
        <div class="cars-list-item__center">
            <div class="cars-list-item-info">
                <div class="cars-list-item-info__header">
                    <div class="cars-list-item-info__header-title">
                        <div class="cars-list-item-info__header-title-value">
                            <div class="cars-list-item__status" :class="['status-' + car.status]">
                                {{ getCarStatusLabel(car.status) }}
                            </div>

                            <router-link :to="{ name: 'ViewCar', params: { car_id: car.id } }">
                                {{ getCarFullName(car) }}
                            </router-link>
                        </div>

                        <template v-if="canEditCar(car) || canSendToModerationCar(car) || canDeleteCar(car) || canRecreateCar(car)">
                            <UIDropDown as-modal-on-mobile>
                                <template #elements>
                                    <router-link
                                        v-if="canEditCar(car)"
                                        :to="{ name: 'EditCar', params: { car_id: car.id } }"
                                        class="ui-dropdown-menu__element"
                                    >
                                        <img v-svg-inline src="@/assets/icons/ui/edit.svg" />
                                        <span>Редактировать</span>
                                    </router-link>

                                    <li
                                        v-if="canSendToModerationCar(car)"
                                        class="ui-dropdown-menu__element"
                                        @click="handleSendToModerationCar"
                                    >
                                        <img v-svg-inline src="@/assets/icons/ui/right-arrow.svg" />
                                        <span>Отправить на согласование</span>
                                    </li>

                                    <li
                                        v-if="canDeleteCar(car)"
                                        class="ui-dropdown-menu__element danger"
                                        @click="handleDeleteCar"
                                    >
                                        <img v-svg-inline src="@/assets/icons/ui/trash.svg" />
                                        <span>Удалить</span>
                                    </li>

                                    <li
                                        v-if="canRecreateCar(car)"
                                        class="ui-dropdown-menu__element"
                                        @click="handleRecreateCar"
                                    >
                                        <img v-svg-inline src="@/assets/icons/ui/ph_copy-simple.svg" />
                                        <span>Пересоздать</span>
                                    </li>
                                </template>
                            </UIDropDown>
                        </template>
                    </div>

                    <div class="cars-list-item__left">
                        <CarImageGalleryStatic :photos="car.photos ?? []" />
                    </div>

                    <div class="cars-list-item-info__header-specs">
                        {{ getCarSpecification(car) }}
                    </div>
                </div>

                <div v-if="car.comment" class="cars-list-item-info__description">
                    {{ car.comment }}
                </div>

                <CarTags v-if="car.tags.length > 0" :tags="car.tags" />
            </div>

            <CarItemDetails
                :details="[
                    { title: 'Тип поступления:', value: getCarAdmissionTypeLabel(car.admission_type) },
                    { title: 'Тип НДС:', value: getCarNdsTypeLabel(car.nds_type) },
                    { title: 'Комплектация:', value: car.complectation.code },
                    { title: 'VIN:', value: car.vin },
                    { title: 'ПТС:', value: car.pts_number },
                    { title: 'Добавлен:', value: formatFromUTC(car.created_at) },
                    { title: 'Дилер:', value: getCarDealerFullName(car) },
                ]"
            />

            <!-- Здесь вопрос по количеству документов и отображению кнопки загрузки доп. документов -->
            <div class="cars-list-item__documents">
                <CarDocuments
                    v-if="canEditCarDocuments(car)"
                    :car-documents="localDocuments.diagnostic_card"
                    type-title="Диагностическая карта"
                    if-no-documents-show-this="file-not-uploaded"
                />

                <CarDocuments
                    v-if="canEditCarDocuments(car)"
                    :car-documents="localDocuments.avtocod_report"
                    type-title="Отчет автокод/автотека"
                    if-no-documents-show-this="file-not-uploaded"
                />
            </div>
        </div>

        <div class="cars-list-item__right">
            <div class="cars-list-item__right-section">
                <div
                    v-if="car.status === CarStatus.Draft
                        || car.status === CarStatus.OnApproval
                        || car.status === CarStatus.OnRevision
                        || car.status === CarStatus.Approved
                        || car.status === CarStatus.NotRealized
                    "
                    class="cars-list-item__auction-info-list"
                >
                    <div class="cars-list-item__auction-info-item">
                        <span class="cars-list-item__auction-info-item-label">Стартовая цена</span>
                        <span class="cars-list-item__auction-info-item-value">{{ numberFormat(car.start_price) }} ₽</span>
                    </div>
                </div>

                <div
                    v-if="car.status === CarStatus.OnAuction && car.lastAuction?.last_lot"
                    class="cars-list-item__auction-info-list"
                >
                    <div class="cars-list-item__auction-info-item">
                        <span class="cars-list-item__auction-info-item-label">Стартовая цена</span>
                        <span class="cars-list-item__auction-info-item-value">{{ numberFormat(car.start_price) }} ₽</span>
                    </div>

                    <div class="cars-list-item__auction-info-item">
                        <span class="cars-list-item__auction-info-item-label">Дата начала</span>
                        <span class="cars-list-item__auction-info-item-value">
                            {{ formatFromUTC(car.lastAuction.last_lot.start_date) }}
                        </span>
                    </div>

                    <div class="cars-list-item__auction-info-item">
                        <span class="cars-list-item__auction-info-item-label">Дата окончания</span>
                        <span class="cars-list-item__auction-info-item-value">
                            {{ formatFromUTC(car.lastAuction.last_lot.end_date) }}
                        </span>
                    </div>
                </div>

                <div
                    v-if="car.status === CarStatus.Sold && car.lastAuction?.last_lot"
                    class="cars-list-item__auction-info-list"
                >
                    <div class="cars-list-item__auction-info-item cars-list-item__auction-info-item-winner">
                        <span class="cars-list-item__auction-info-item-label">Победитель</span>
                        <span class="cars-list-item__auction-info-item-value">{{ car.lastAuction.last_lot.winner.dealer_name }}</span>
                    </div>

                    <div class="cars-list-item__auction-info-item">
                        <span class="cars-list-item__auction-info-item-label">Дата начала</span>
                        <span class="cars-list-item__auction-info-item-value">
                            {{ formatFromUTC(car.lastAuction.last_lot.start_date) }}
                        </span>
                    </div>

                    <div class="cars-list-item__auction-info-item">
                        <span class="cars-list-item__auction-info-item-label">Дата окончания</span>
                        <span class="cars-list-item__auction-info-item-value">
                            {{ formatFromUTC(car.lastAuction.last_lot.end_date) }}
                        </span>
                    </div>

                    <div class="cars-list-item__auction-info-item">
                        <span class="cars-list-item__auction-info-item-label">Стартовая цена</span>
                        <span class="cars-list-item__auction-info-item-value">{{ numberFormat(car.start_price) }} ₽</span>
                    </div>

                    <div class="cars-list-item__auction-info-item">
                        <span class="cars-list-item__auction-info-item-label">Ставка победителя</span>
                        <span class="cars-list-item__auction-info-item-value">
                            {{ numberFormat(car.lastAuction.last_lot.winner.bid_value ?? 0) }} ₽
                        </span>
                    </div>
                </div>

                <div class="cars-list-item__actions">
                    <UISimpleButton
                        v-if="canApproveCar(car)"
                        @clicked="onApprove"
                    >
                        Согласовать
                    </UISimpleButton>

                    <UISimpleButton
                        v-if="canRejectCar(car)"
                        button-type="outlined"
                        @clicked="onReject"
                    >
                        Отклонить
                    </UISimpleButton>

                    <router-link
                        v-if="car.status === CarStatus.OnAuction && car.lastAuction?.last_lot"
                        :to="{ name: 'ViewAuction', params: { auction_id: car.lastAuction?.last_lot.id } }"
                    >
                        <UISimpleButton class="auction-list-header__action">
                            <template #icon-right>
                                <img v-svg-inline src="@/assets/icons/ui/right-arrow.svg" />
                            </template>
                            В аукцион
                        </UISimpleButton>
                    </router-link>

                    <UIDropDown
                        v-if="canViewCarSaleContract(car) || canViewCarInvoice(car)"
                        class="cars-list-item__documents-dropdown"
                    >
                        <template #trigger>
                            <UISimpleButton>
                                <template #icon>
                                    <img v-svg-inline src="@/assets/icons/report.svg" />
                                </template>

                                <template #icon-right>
                                    <img v-svg-inline src="@/assets/icons/ui/select-arrow.svg" />
                                </template>
                                Документы
                            </UISimpleButton>
                        </template>

                        <template #elements>
                            <li
                                v-if="canViewCarSaleContract(car)"
                                class="ui-dropdown-menu__element"
                                @click="onDownloadSaleContract"
                            >
                                <img v-svg-inline src="@/assets/icons/ui/document.svg" />
                                <span>ДКП</span>
                            </li>

                            <li
                                v-if="canViewCarInvoice(car)"
                                class="ui-dropdown-menu__element"
                                @click="onDownloadInvoice"
                            >
                                <img v-svg-inline src="@/assets/icons/ui/document.svg" />
                                <span>Счет на оплату</span>
                            </li>
                        </template>
                    </UIDropDown>
                </div>
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
    import { computed, defineEmits, h, inject, onMounted, ref } from 'vue';

    import router from '@/router';
    import { ModalPlugin } from '@/plugins/modalPlugin.ts';

    import {
        Car,
        CarDocument,
        CarDocumentType,
        CarRejectForm,
        CarSendForModerationForm,
        CarStatus,
        getCarAdmissionTypeLabel,
        getCarNdsTypeLabel,
        getCarStatusLabel,
    } from '@/types/car.type.ts';

    import useCarRepository from '@/repositories/car/useCarRepository.ts';
    import useDocumentDownloader from '@/utils/useDocumentDownloader.ts';
    import useNotifications from '@/utils/useNotifications.ts';
    import usePermission from '@/repositories/auth/usePermission.ts';

    import { numberFormat } from '@/utils/NumberFormatter.ts';
    import { formatFromUTC } from '@/utils/DateTimeFormatter.ts';
    import { getCarDealerFullName, getCarFullName, getCarSpecification } from '@/utils/car';

    import UIDropDown from '@/components/ui/UIDropDown/UIDropDown.vue';
    import UISimpleButton from '@/components/ui/UISimpleButton/UISimpleButton.vue';

    import ConfirmActionModal from '@/components/modals/ConfirmActionModal/ConfirmActionModal.vue';

    import CarConfirmApprove from '@/components/cars/CarConfirmApprove/CarConfirmApprove.vue';
    import CarConfirmDelete from '@/components/cars/CarConfirmDelete/CarConfirmDelete.vue';
    import CarConfirmSendToModeration from '@/components/cars/CarConfirmSendToModeration/CarConfirmSendToModeration.vue';
    import CarConfirmRecreate from '@/components/cars/CarConfirmRecreate/CarConfirmRecreate.vue';
    import CarConfirmReject from '@/components/cars/CarConfirmReject/CarConfirmReject.vue';
    import CarDocuments from '@/components/cars/CarDocuments/CarDocuments.vue';
    import CarItemDetails from '@/components/cars/CarItemDetails/CarItemDetails.vue';
    import CarImageGalleryStatic from '@/components/cars/CarImageGalleryStatic/CarImageGalleryStatic.vue';
    import CarTags from '@/components/cars/CarTags/CarTags.vue';

    const { car } = defineProps<{
        car: Car,
    }>();

    const emit = defineEmits<{
        (e: 'updated'): void,
    }>();

    const $modal = inject('$modal') as typeof ModalPlugin;

    const {
        canEditCar,
        canDeleteCar,
        canApproveCar,
        canRejectCar,
        canEditCarDocuments,
        canViewCarInvoice,
        canViewCarSaleContract,
        canRecreateCar,
        canSendToModerationCar,
    } = usePermission();
    const { approveCar, rejectCar, recreateCar, deleteCar, sendToModerationCar } = useCarRepository();
    const { downloadAuctionInvoice, downloadAuctionSaleContract } = useDocumentDownloader();
    const { showSuccess } = useNotifications();

    /**
     * Локальные документы
     * При монтировании копируем в них данные из carDocumentsComputed.
     */
    const localDocuments = ref<Record<CarDocumentType, CarDocument[]>>({
        [CarDocumentType.AvtocodReport]: [],
        [CarDocumentType.DiagnosticCard]: [],
        [CarDocumentType.Document]: [],
    });

    /**
     * ТЕСТОВЫЕ ДАННЫЕ
     * Эти документы будут получаться с сервера через car.documents.
     */
    const carDocumentsComputed = computed<Record<CarDocumentType, CarDocument[]>>(() => {
        const docs: Record<CarDocumentType, CarDocument[]> = {
            [CarDocumentType.AvtocodReport]: [],
            [CarDocumentType.DiagnosticCard]: [],
            [CarDocumentType.Document]: [],
        };

        if (car.documents && car.documents.length > 0) {
            car.documents.forEach((doc: CarDocument) => {
                docs[doc.type].push({ ...doc });
            });
        }

        return docs;
    });

    function onApprove(): void {
        $modal.open({
            component: ConfirmActionModal,
            modalType: 'narrow',
            slots: {
                default: () =>
                    h(CarConfirmApprove, { car }),
            },
            props: {
                onConfirm: async (): Promise<unknown> => {
                    try {
                        await approveCar(car.id);
                    }
                    catch (errors) {
                        return errors;
                    }

                    showSuccess({
                        title: 'Автомобиль согласован',
                    });

                    emit('updated');
                },
            },
        });
    }

    function onReject(): void {
        $modal.open({
            component: ConfirmActionModal,
            modalType: 'narrow',
            slots: {
                default: () =>
                    h(CarConfirmReject, { car }),
            },
            props: {
                onConfirm: async (form: CarRejectForm): Promise<unknown> => {
                    try {
                        await rejectCar(car.id, form.comment);
                    }
                    catch (errors) {
                        return errors;
                    }

                    showSuccess({
                        title: 'Автомобиль отклонен',
                    });

                    emit('updated');
                },
            },
        });
    }

    function handleDeleteCar(): void {
        $modal.open({
            component: ConfirmActionModal,
            modalType: 'narrow',
            slots: {
                default: () =>
                    h(CarConfirmDelete, { car: car }),
            },
            props: {
                onConfirm: async () => {
                    await deleteCar(car.id);

                    showSuccess({
                        title: 'Автомобиль удален',
                    });

                    emit('updated');
                },
            },
        });
    }

    function handleRecreateCar(): void {
        $modal.open({
            component: ConfirmActionModal,
            modalType: 'narrow',
            slots: {
                default: () =>
                    h(CarConfirmRecreate, { car }),
            },
            props: {
                onConfirm: async () => {
                    const recreatedCarId = await recreateCar(car.id);

                    showSuccess({
                        title: 'Автомобиль пересоздан',
                    });

                    router.push({ name: 'ViewCar', params: { car_id: recreatedCarId } });
                },
            },
        });
    }

    function handleSendToModerationCar(): void {
        $modal.open({
            component: ConfirmActionModal,
            modalType: 'narrow',
            slots: {
                default: () =>
                    h(CarConfirmSendToModeration, { car }),
            },
            props: {
                onConfirm: async (form: CarSendForModerationForm): Promise<unknown> => {
                    try {
                        await sendToModerationCar(car.id, form);
                    }
                    catch (errors) {
                        return errors;
                    }

                    showSuccess({
                        title: 'Автомобиль отправлен на согласование',
                    });

                    emit('updated');
                },
            },
        });
    }

    function onDownloadSaleContract(): void {
        if (car.lastAuction?.last_lot?.id) {
            downloadAuctionSaleContract(car.lastAuction.last_lot.id, `auction-${car.lastAuction.last_lot.id}-sale-contract.docx`);
        }
    }

    function onDownloadInvoice(): void {
        if (car.lastAuction?.last_lot?.id) {
            downloadAuctionInvoice(car.lastAuction.last_lot.id, `auction-${car.lastAuction.last_lot.id}-invoice.xlsx`);
        }
    }

    onMounted(() => {
        localDocuments.value = {
            [CarDocumentType.AvtocodReport]: [...carDocumentsComputed.value[CarDocumentType.AvtocodReport]],
            [CarDocumentType.DiagnosticCard]: [...carDocumentsComputed.value[CarDocumentType.DiagnosticCard]],
            [CarDocumentType.Document]: [...carDocumentsComputed.value[CarDocumentType.Document]],
        };
    });
</script>
